import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiMail, FiServer, FiLock, FiUser, FiSend, FiCheckCircle, FiAlertCircle, FiCloud } = FiIcons;

const SmtpSettings = () => {
  const { settings, updateSettings, sendEmail, sentEmails } = useData();
  const [formData, setFormData] = useState(settings.smtp);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (settings.smtp) setFormData(settings.smtp);
  }, [settings.smtp]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings('smtp', formData);
    setStatusMessage('Settings saved.');
    setTimeout(() => setStatusMessage(''), 3000);
  };

  const handleTestEmail = async () => {
    if (!testEmail) return alert("Enter recipient email.");
    setTestStatus('sending');
    setStatusMessage('Connecting to SMTP...');
    
    // Pass current form data as override config to test unsaved changes
    const result = await sendEmail(testEmail, "Test Email from CRM", "This is a test email sent from the CRM system to verify your SMTP settings.", formData);
    
    if (result.success) {
      setTestStatus('success');
      setStatusMessage(result.message);
    } else {
      setTestStatus('error');
      setStatusMessage(result.error);
    }
    
    setTimeout(() => { if(result.success) setTestStatus(null); }, 5000);
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <SafeIcon icon={FiMail} className="w-5 h-5 mr-2 text-blue-600" /> SMTP Configuration
          </h2>
          <div className="flex items-center space-x-2 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700">
            <SafeIcon icon={FiCloud} className="w-3 h-3" />
            <span>Production Mode (Real SMTP)</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
              <div className="relative">
                <SafeIcon icon={FiServer} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" name="host" value={formData.host} onChange={handleChange} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg" placeholder="smtp.gmail.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
              <input type="number" name="port" value={formData.port} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="587" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" name="username" value={formData.username} onChange={handleChange} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg" placeholder="email@example.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="password" name="password" value={formData.password} onChange={handleChange} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg" placeholder="••••••••" />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
                <input type="text" name="fromName" value={formData.fromName} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="CRM System" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
                <select name="encryption" value={formData.encryption} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  <option value="tls">TLS (STARTTLS)</option>
                  <option value="ssl">SSL (Secure)</option>
                  <option value="none">None</option>
                </select>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center pt-4 border-t border-gray-100 gap-4">
            <div className="flex items-center space-x-3 flex-1 w-full">
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="recipient@example.com" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <button type="button" onClick={handleTestEmail} disabled={testStatus === 'sending'} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium whitespace-nowrap">
                {testStatus === 'sending' ? 'Sending...' : 'Test Connection'}
              </button>
            </div>
            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">Save Config</button>
          </div>
          
          {statusMessage && (
            <div className={`mt-2 text-sm p-3 rounded-lg flex items-center justify-center border ${testStatus === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
              {testStatus === 'error' ? <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" /> : <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-2" />}
              {statusMessage}
            </div>
          )}
        </form>
      </motion.div>

      {/* Outbox Log */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center">
            <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" /> Sent Emails
          </h3>
          <span className="text-xs text-gray-500">History</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {sentEmails.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2">To</th>
                  <th className="px-4 py-2">Subject</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sentEmails.map(email => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{email.to}</td>
                    <td className="px-4 py-3 text-gray-600 truncate max-w-xs">{email.subject}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {formatDistanceToNow(new Date(email.sentAt), { addSuffix: true })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${email.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {email.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="p-8 text-center text-gray-500 text-sm">No emails sent yet.</div>}
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings;